/**
 * Category navigation dropdowns
 *
 * Cartzilla provides a generic dropdown-hover component, but category
 * navigation needs additional positioning behaviour. Submenus should open
 * next to their parent item while staying within the vertical bounds of the
 * main category menu whenever possible.
 *
 * Bootstrap does not apply Popper positioning to dropdowns inside navbars,
 * so the required vertical correction is calculated here.
 *
 * Keeping this logic in a separate component avoids modifying Cartzilla's
 * dropdown-hover.js and makes future Cartzilla updates easier to apply.
 */

import Dropdown from 'bootstrap/js/dist/dropdown.js'

const CategoryDropdown = {
  selector: '[data-category-dropdown]',
  instances: [],

  init() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return
    }

    document.querySelectorAll(this.selector).forEach((trigger) => {
      this.initDropdown(trigger)
    })

    if (this.instances.length === 0) {
      return
    }

    window.addEventListener('resize', () => this.positionOpenDropdowns())

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.instances.forEach(({ dropdown }) => dropdown.hide())
      }
    })

    document.addEventListener('focusin', (event) => {
      this.instances.forEach(({ dropdownItem, dropdown }) => {
        if (!dropdownItem.contains(event.target) && !dropdownItem.matches(':hover')) {
          dropdown.hide()
        }
      })
    })
  },

  initDropdown(trigger) {
    if (trigger.dataset.categoryDropdownInitialized === 'true') {
      return
    }

    const categoryMenu = trigger.closest('.category-menu')
    const dropdownItem = trigger.closest('.dropend')
    const submenu = dropdownItem?.querySelector(':scope > .category-submenu')

    if (!categoryMenu || !dropdownItem || !submenu) {
      return
    }

    const dropdown = Dropdown.getOrCreateInstance(trigger)

    trigger.dataset.categoryDropdownInitialized = 'true'

    this.instances.push({
      trigger,
      categoryMenu,
      dropdownItem,
      submenu,
      dropdown,
    })

    trigger.addEventListener('mouseover', () => {
      trigger.dataset.bsToggle = 'dropdown-hover'
      dropdown.show()
      this.positionDropdown(categoryMenu, submenu)
      trigger.blur()
    })

    dropdownItem.addEventListener('mouseleave', () => {
      trigger.dataset.bsToggle = 'dropdown'
      dropdown.hide()
    })

    trigger.addEventListener('focus', () => {
      dropdown.show()
      this.positionDropdown(categoryMenu, submenu)
    })
  },

  positionDropdown(categoryMenu, submenu) {
    submenu.style.setProperty('--category-submenu-adjustment-y', '0px')

    const categoryMenuRect = categoryMenu.getBoundingClientRect()
    const submenuRect = submenu.getBoundingClientRect()

    let adjustment = 0

    if (submenuRect.bottom > categoryMenuRect.bottom) {
      adjustment -= submenuRect.bottom - categoryMenuRect.bottom
    }

    if (submenuRect.top + adjustment < categoryMenuRect.top) {
      adjustment += categoryMenuRect.top - (submenuRect.top + adjustment)
    }

    submenu.style.setProperty('--category-submenu-adjustment-y', `${Math.round(adjustment)}px`)
  },

  positionOpenDropdowns() {
    this.instances.forEach(({ categoryMenu, submenu }) => {
      if (!submenu.classList.contains('show')) {
        return
      }

      this.positionDropdown(categoryMenu, submenu)
    })
  },
}

export default CategoryDropdown
